import { Add, Delete } from '@mui/icons-material';
import { Alert, Box, InputLabel, MenuItem, Stack } from '@mui/material';
import { DateTime } from 'luxon';
import { SmartCallProfile } from './CloudConnectorSmartCall.tsx';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import { EmDash } from 'dg-web-shared/lib/Punctuation.ts';
import { Clickable } from 'dg-web-shared/ui/Clickable.tsx';
import {
    FieldArrayWithId,
    useFieldArray,
    useForm,
    useWatch,
} from 'react-hook-form';
import { ReactHookFormTextField } from 'dg-web-shared/common/components/material-ui/react-hook-form-fields/ReactHookFormTextField.tsx';
import {
    ConfirmationDialog,
    ConfirmationDialogVersions,
} from '../operator-logins/components/ConfirmationDialog.tsx';
import { Typo } from 'dg-web-shared/ui/typo.ts';
import { type JSX } from 'react';

export const smartCallTimeWindowMaxConfigs = 5;

export enum SmartCallTimeWindowFields {
    smartCallProfileId = 'smartCallProfileId',
    start = 'start',
    end = 'end',
}

export interface SmartCallTimeWindowConfig {
    [SmartCallTimeWindowFields.smartCallProfileId]: string;
    [SmartCallTimeWindowFields.start]: string;
    [SmartCallTimeWindowFields.end]: string;
}

enum SmartCallTimeWindowFormFields {
    windows = 'windows',
}

type SmartCallTimeWindowFieldValues = {
    [SmartCallTimeWindowFormFields.windows]: SmartCallTimeWindowConfig[];
};

const emptyTimeWindowConfigs: SmartCallTimeWindowConfig[] = [];

export function createEmptyWindowConfig(): SmartCallTimeWindowConfig {
    return {
        [SmartCallTimeWindowFields.smartCallProfileId]: '',
        [SmartCallTimeWindowFields.start]: '',
        [SmartCallTimeWindowFields.end]: '',
    };
}

export function isIncompleteSmartCallTimeWindowConfig(
    timeWindowConfig: SmartCallTimeWindowConfig | undefined,
): boolean {
    return (
        !!timeWindowConfig &&
        (!timeWindowConfig.smartCallProfileId ||
            !timeWindowConfig.start ||
            !timeWindowConfig.end)
    );
}

export function sortSmartCallTimeWindowConfigs(
    timeWindowConfigs: SmartCallTimeWindowConfig[],
): SmartCallTimeWindowConfig[] {
    return [...timeWindowConfigs].sort((a, b) =>
        a.start.localeCompare(b.start),
    );
}

export function hasOverlappingTimeWindows(
    timeWindowConfigs: SmartCallTimeWindowConfig[],
) {
    const sortedTimeWindowConfigs =
        sortSmartCallTimeWindowConfigs(timeWindowConfigs);
    for (let i = 0; i < sortedTimeWindowConfigs.length - 1; i++) {
        const currentTimeWindow = sortedTimeWindowConfigs[i];
        const nextTimeWindow = sortedTimeWindowConfigs[i + 1];

        const nextIsLastTimeWindow = sortedTimeWindowConfigs.length === i + 2;

        if (
            nextIsLastTimeWindow &&
            isIncompleteSmartCallTimeWindowConfig(nextTimeWindow)
        ) {
            return false;
        }

        if (currentTimeWindow.end > nextTimeWindow.start) {
            return true;
        }
    }
    return false;
}

function parseTimeToMinutes(time: string): number {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
}

export function hasFullDayConfigured(
    configs: SmartCallTimeWindowConfig[],
): boolean {
    if (!configs.length) return false;

    const intervals = configs
        .map(config => ({
            start: parseTimeToMinutes(config[SmartCallTimeWindowFields.start]),
            // we add one minute to the end since it will be rounded up (e.g. 10:59 is until the end of the hour)
            end: parseTimeToMinutes(config[SmartCallTimeWindowFields.end]) + 1,
        }))
        .sort((a, b) => a.start - b.start);

    if (intervals[0].start !== 0) return false;

    for (let i = 0; i < intervals.length - 1; i++) {
        if (intervals[i].end !== intervals[i + 1].start) {
            return false;
        }
    }
    const minutesPerDay = 1440;
    return intervals[intervals.length - 1].end === minutesPerDay;
}

export function generateTimeOptions(
    start: string,
    end: string,
    minutesInterval: number,
): string[] {
    const options: string[] = [];
    let time = DateTime.fromFormat(start, 'HH:mm');
    const endTime = DateTime.fromFormat(end, 'HH:mm');

    while (time <= endTime) {
        options.push(time.toFormat('HH:mm'));
        time = time.plus({ minutes: minutesInterval });
    }

    return options;
}

export function SmartCallTimeWindowConfigEditDialog({
    title,
    children,
    profiles,
    timeWindowConfigs,
    onConfirm,
    onCancel,
}: {
    title: JSX.Element;
    children?: JSX.Element;
    profiles: SmartCallProfile[];
    timeWindowConfigs: SmartCallTimeWindowConfig[];
    onConfirm: (timeWindowConfigs: SmartCallTimeWindowConfig[]) => void;
    onCancel: () => void;
}) {
    const initialFormTimeWindowConfigs =
        timeWindowConfigs.length >= smartCallTimeWindowMaxConfigs
            ? timeWindowConfigs
            : [...timeWindowConfigs, createEmptyWindowConfig()];

    const { control, watch } = useForm<SmartCallTimeWindowFieldValues>({
        defaultValues: {
            [SmartCallTimeWindowFormFields.windows]:
                initialFormTimeWindowConfigs,
        },
    });

    const { fields, remove, replace } = useFieldArray({
        control,
        name: SmartCallTimeWindowFormFields.windows,
    });

    const configuredTimeWindowConfigs: SmartCallTimeWindowConfig[] =
        useWatch({
            control,
            name: SmartCallTimeWindowFormFields.windows,
        }) ?? emptyTimeWindowConfigs;

    const hasOverlappingTimeWindowsError = hasOverlappingTimeWindows(
        configuredTimeWindowConfigs,
    );
    const startTimeOptions = generateTimeOptions('00:00', '23:30', 30);
    const endTimeOptions = generateTimeOptions('00:29', '23:59', 30);

    return (
        <ConfirmationDialog
            open={true}
            confirmLabel={
                <Localized
                    de="Bestätigen"
                    fr="Confirmer"
                    it="Confermare"
                    en="Confirm"
                />
            }
            confirmDisabled={hasOverlappingTimeWindowsError}
            onConfirm={() => {
                if (hasOverlappingTimeWindowsError) {
                    return;
                }

                const currentTimeWindows = watch(
                    SmartCallTimeWindowFormFields.windows,
                );
                const lastTimeWindow =
                    currentTimeWindows[currentTimeWindows.length - 1];
                const configuredTimeWindows =
                    isIncompleteSmartCallTimeWindowConfig(lastTimeWindow)
                        ? currentTimeWindows.slice(0, -1)
                        : currentTimeWindows;

                onConfirm(
                    sortSmartCallTimeWindowConfigs(configuredTimeWindows),
                );
            }}
            onCancel={onCancel}
            title={title}
            dialogVersion={ConfirmationDialogVersions.PRIMARY}
        >
            <Box sx={{ paddingBottom: '20px', ...Typo.robotoMedium }}>
                {hasOverlappingTimeWindowsError && (
                    <Alert severity="error" sx={{ marginX: 0, marginY: 1 }}>
                        <Localized
                            de="Zeitfenster dürfen sich nicht überschneiden."
                            fr="Les plages horaires ne doivent pas se chevaucher."
                            it="Le fasce orarie non devono sovrapporsi."
                            en="Time windows must not overlap."
                        />
                    </Alert>
                )}
                {children}
                <SmartCallConfiguredTimeWindows
                    fields={fields}
                    profiles={profiles}
                    timeWindowConfigs={configuredTimeWindowConfigs}
                    onRemove={index => remove(index)}
                />
                <Box sx={{ paddingTop: '10px' }}>
                    <InputLabel>
                        <Localized
                            de="Neues Zeitfenster"
                            fr="Nouvelle plage horaire"
                            it="Nuova fascia oraria"
                            en="New time window"
                        />
                    </InputLabel>

                    {(() => {
                        const lastEntryIndex = fields.length - 1;
                        const config: SmartCallTimeWindowConfig | undefined =
                            configuredTimeWindowConfigs[lastEntryIndex];

                        if (!config) {
                            return null;
                        }

                        return (
                            <Stack
                                direction="row"
                                spacing={2}
                                sx={{
                                    alignItems: 'center',
                                }}
                            >
                                <ReactHookFormTextField
                                    label={
                                        <Localized
                                            de="Profil"
                                            fr="Profil"
                                            it="Profilo"
                                            en="Profile"
                                        />
                                    }
                                    control={control}
                                    name={`${SmartCallTimeWindowFormFields.windows}.${lastEntryIndex}.${SmartCallTimeWindowFields.smartCallProfileId}`}
                                    select
                                    value={
                                        config[
                                            SmartCallTimeWindowFields
                                                .smartCallProfileId
                                        ]
                                    }
                                    sx={{ flex: 2 }}
                                >
                                    {profiles.map(profile => (
                                        <MenuItem
                                            key={profile.id}
                                            value={profile.id}
                                        >
                                            {profile.description}
                                        </MenuItem>
                                    ))}
                                </ReactHookFormTextField>
                                <ReactHookFormTextField
                                    label={
                                        <Localized
                                            de="Von"
                                            fr="De"
                                            it="Da"
                                            en="From"
                                        />
                                    }
                                    control={control}
                                    name={`${SmartCallTimeWindowFormFields.windows}.${lastEntryIndex}.${SmartCallTimeWindowFields.start}`}
                                    select
                                    value={
                                        config[SmartCallTimeWindowFields.start]
                                    }
                                    sx={{ flex: 1 }}
                                >
                                    {startTimeOptions.map(time => (
                                        <MenuItem key={time} value={time}>
                                            {time}
                                        </MenuItem>
                                    ))}
                                </ReactHookFormTextField>
                                <ReactHookFormTextField
                                    label={
                                        <Localized
                                            de="Bis"
                                            fr="À"
                                            it="A"
                                            en="To"
                                        />
                                    }
                                    control={control}
                                    name={`${SmartCallTimeWindowFormFields.windows}.${lastEntryIndex}.${SmartCallTimeWindowFields.end}`}
                                    select
                                    disabled={!config.start}
                                    value={
                                        config[SmartCallTimeWindowFields.end]
                                    }
                                    sx={{ flex: 1 }}
                                >
                                    {endTimeOptions
                                        .filter(t => t > config.start)
                                        .map(time => (
                                            <MenuItem key={time} value={time}>
                                                {time}
                                            </MenuItem>
                                        ))}
                                </ReactHookFormTextField>
                                {configuredTimeWindowConfigs.length <
                                    smartCallTimeWindowMaxConfigs && (
                                    <Clickable
                                        element="div"
                                        onClick={() => {
                                            const lastTimeWindow =
                                                configuredTimeWindowConfigs[
                                                    configuredTimeWindowConfigs.length -
                                                        1
                                                ];

                                            if (
                                                isIncompleteSmartCallTimeWindowConfig(
                                                    lastTimeWindow,
                                                ) ||
                                                configuredTimeWindowConfigs.length >=
                                                    smartCallTimeWindowMaxConfigs
                                            ) {
                                                return;
                                            }

                                            replace([
                                                ...sortSmartCallTimeWindowConfigs(
                                                    configuredTimeWindowConfigs,
                                                ),
                                                createEmptyWindowConfig(),
                                            ]);
                                        }}
                                    >
                                        <Add />
                                    </Clickable>
                                )}
                            </Stack>
                        );
                    })()}
                </Box>
            </Box>
        </ConfirmationDialog>
    );
}

export function SmartCallTimeWindowConfigDisplay({
    timeWindow,
    profiles,
}: {
    timeWindow: SmartCallTimeWindowConfig;
    profiles: SmartCallProfile[];
}) {
    const profile = profiles.find(
        profile => profile.id === timeWindow.smartCallProfileId,
    );

    if (!profile) {
        return (
            <Alert severity="error">
                <Localized
                    de="Das ausgewählte Profil fehlt"
                    fr="Le profil sélectionné est manquant"
                    it="Il profilo selezionato è mancante"
                    en="The selected profile is missing"
                />
            </Alert>
        );
    }

    return (
        <Stack spacing={3} direction="row">
            <Box>{`${timeWindow.start} ${EmDash} ${timeWindow.end}`}</Box>
            <Box>{profile.description}</Box>
        </Stack>
    );
}

export function SmartCallConfiguredTimeWindows({
    fields,
    profiles,
    timeWindowConfigs,
    onRemove,
}: {
    fields: FieldArrayWithId[];
    profiles: SmartCallProfile[];
    timeWindowConfigs: SmartCallTimeWindowConfig[];
    onRemove: (index: number) => void;
}) {
    if (fields.length <= 1) {
        return null;
    }

    return (
        <Box sx={{ paddingTop: '10px' }}>
            <InputLabel>
                <Localized
                    de="Individuelle Zeitfenster"
                    fr="Plages horaires individuelles"
                    it="Fasce orarie individuali"
                    en="Individual time windows"
                />
            </InputLabel>

            {fields.slice(0, -1).map((field, index) => {
                const config = timeWindowConfigs[index];
                const profile = profiles.find(
                    p => p.id === config?.smartCallProfileId,
                );

                if (!config) {
                    return null;
                }

                return (
                    <Stack key={field.id} spacing={3} direction="row">
                        <Box
                            sx={{
                                flex: 1,
                                display: 'flex',
                                gap: 3,
                            }}
                        >
                            <Box>{`${config.start} ${EmDash} ${config.end}`}</Box>
                            <Box>{profile?.description ?? EmDash}</Box>
                        </Box>

                        <Clickable
                            element="div"
                            onClick={() => onRemove(index)}
                        >
                            <Delete />
                        </Clickable>
                    </Stack>
                );
            })}
        </Box>
    );
}
